import { useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { Header } from './components/Header';
import { SearchBar } from './components/SearchBar';
import { AlphabetNav } from './components/AlphabetNav';
import { FilterBar } from './components/FilterBar';
import { TermsGrid } from './components/TermsGrid';
import { Modal } from './components/Modal';
import { Footer } from './components/Footer';
import { DefinitionPage } from './components/DefinitionPage';
import { DefinitionPageCyberpunk } from './components/DefinitionPageCyberpunk';
import DictionaryHomeCyberpunk from './components/DictionaryHomeCyberpunk';
import { ModalCyberpunk } from './components/ModalCyberpunk';
import { useDictionary, useKeyboardShortcut } from './hooks';
import { config } from './data/config';
import { DictionaryEntry } from './types';
import { ViewModeProvider, useViewMode } from './context/ViewModeContext';
import { LoginPage } from './components/admin/LoginPage';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { ProtectedRoute } from './components/admin/ProtectedRoute';
import { EntryForm } from './components/admin/EntryForm';

function DictionaryApp() {
  const [selectedEntry, setSelectedEntry] = useState<DictionaryEntry | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { mode, designVersion } = useViewMode();
  const navigate = useNavigate();

  const {
    data,
    loading,
    searchQuery,
    selectedTag,
    selectedLetter,
    filteredData,
    handleSearch,
    handleTagFilter,
    handleLetterFilter,
    getRandomEntry,
    totalCount,
    visibleCount
  } = useDictionary();

  const handleTermClick = (entry: DictionaryEntry) => {
    console.log('[DEBUG] handleTermClick called:', { mode, designVersion, entryTerm: entry.term });
    if (mode === 'page') {
      console.log('[DEBUG] Navigating to page');
      navigate(`/definition/${encodeURIComponent(entry.term.toLowerCase())}`);
    } else {
      console.log('[DEBUG] Opening modal');
      setSelectedEntry(entry);
    }
  };

  const handleRandomClick = () => {
    const randomEntry = getRandomEntry();
    if (randomEntry) {
      handleTermClick(randomEntry);
    }
  };

  // Raccourcis clavier
  useKeyboardShortcut([
    {
      key: 'k',
      cmd: true,
      callback: () => searchInputRef.current?.focus()
    },
    {
      key: 'k',
      ctrl: true,
      callback: () => searchInputRef.current?.focus()
    },
    {
      key: 'r',
      callback: () => {
        if (document.activeElement !== searchInputRef.current) {
          handleRandomClick();
        }
      }
    },
    {
      key: 'Escape',
      callback: () => setSelectedEntry(null)
    }
  ]);

  return (
    <div className={`min-h-screen flex flex-col ${designVersion === 'cyberpunk' ? 'bg-slate-950' : 'bg-slate-50'}`}>
      {designVersion === 'cyberpunk' ? (
        <DictionaryHomeCyberpunk
          onTermClick={handleTermClick}
          searchInputRef={searchInputRef}
        />
      ) : (
        <>
          <Header />

          <SearchBar
            searchQuery={searchQuery}
            onSearch={handleSearch}
            onRandom={handleRandomClick}
            showRandomButton={config.features.showRandomButton}
            inputRef={searchInputRef}
          />

          <AlphabetNav
            selectedLetter={selectedLetter}
            onLetterClick={handleLetterFilter}
            data={data}
          />

          {config.features.showStats && (
            <FilterBar
              selectedTag={selectedTag}
              onTagSelect={handleTagFilter}
              totalCount={totalCount}
              visibleCount={visibleCount}
            />
          )}

          <main className="max-w-6xl mx-auto px-6 py-8 flex-1 w-full relative">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-32 gap-6">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-rose-100 rounded-full"></div>
                  <div className="w-16 h-16 border-4 border-transparent border-t-rose-500 rounded-full animate-spin absolute top-0 left-0"></div>
                </div>
                <p className="text-slate-400 font-serif italic text-xl animate-pulse">Chargement du dictionnaire...</p>
              </div>
            ) : (
              <TermsGrid
                entries={filteredData}
                onTermClick={handleTermClick}
                showLetterWatermark={config.ui.showLetterWatermark}
              />
            )}
          </main>
        </>
      )}

      {config.features.enableModal && (
        designVersion === 'cyberpunk' ? (
          <ModalCyberpunk
            entry={selectedEntry}
            onClose={() => setSelectedEntry(null)}
          />
        ) : (
          <Modal
            entry={selectedEntry}
            onClose={() => setSelectedEntry(null)}
          />
        )
      )}

      <Footer />
    </div>
  );
}

const DefinitionPageWrapper = () => {
  const { designVersion } = useViewMode();
  return designVersion === 'cyberpunk' ? <DefinitionPageCyberpunk /> : <DefinitionPage />;
};

function App() {
  return (
    <ViewModeProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<DictionaryApp />} />
          <Route path="/definition/:slug" element={<DefinitionPageWrapper />} />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<LoginPage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/new"
            element={
              <ProtectedRoute>
                <EntryForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/edit/:id"
            element={
              <ProtectedRoute>
                <EntryForm />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </ViewModeProvider>
  );
}

export default App;
